import { Button, cn, Input, Label } from '@crm/ui';
import { SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Autocomplete, type AutocompleteOption } from '@/components';
import { useSession } from '@/modules/auth/hooks';
import type { ReportFilters } from '../api';

interface ReportFiltersBarProps {
  value: ReportFilters;
  onChange: (filters: ReportFilters) => void;
  /**
   * Filtro proprio da tela (ex.: situacao do atendimento). Entra junto dos
   * campos sempre visiveis, antes do botao - filtro nenhum fica depois dele.
   */
  extra?: React.ReactNode;
}

/** `date` trabalha em horario local e sem timezone. */
function toInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromInput(value: string, endOfDay: boolean): Date {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year ?? 2026, (month ?? 1) - 1, day ?? 1);

  date.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, 0);

  return date;
}

/**
 * Filtros comuns aos relatorios. A unidade vem primeiro, como em toda tela do
 * sistema, e so aparece para quem atende mais de uma.
 */
export function ReportFiltersBar({ value, onChange, extra }: ReportFiltersBarProps) {
  const { user } = useSession();
  const units = user?.units ?? [];
  const [isOpen, setOpen] = useState(false);

  const set = (patch: Partial<ReportFilters>) => onChange({ ...value, ...patch });

  const single = (options: AutocompleteOption[]) => options[0]?.id;

  const avancados =
    (value.unitId ? 1 : 0) + (value.professionalId ? 1 : 0) + (value.serviceId ? 1 : 0);

  // No celular ficam so as datas - que e o que todo relatorio pergunta
  // primeiro; unidade, profissional e servico entram sob demanda, senao o
  // formulario de filtro toma a tela inteira antes de qualquer numero.
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
      <div className="space-y-2">
        <Label htmlFor="report-from">De</Label>
        <Input
          id="report-from"
          type="date"
          value={toInput(value.from)}
          onChange={(event) => set({ from: fromInput(event.target.value, false) })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="report-to">Ate</Label>
        <Input
          id="report-to"
          type="date"
          value={toInput(value.to)}
          onChange={(event) => set({ to: fromInput(event.target.value, true) })}
        />
      </div>

      {extra}

      <Button
        variant="outline"
        onClick={() => setOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label="Busca avancada"
        // Vale tambem no desktop: cinco campos e quatro caixas de marcar
        // empurravam os numeros do relatorio para fora da primeira dobra.
        // `order-last` mantem o botao no fim dos filtros, nunca no meio.
        className={cn(
          // No celular so o icone, quadrado; do tablet em diante entra o
          // rotulo. `self-end` alinha o botao pela base dos campos, nao pelo
          // topo do rotulo deles.
          'order-last col-span-2 h-11 w-11 justify-center gap-2 justify-self-end self-end px-0 sm:col-span-1 sm:w-auto sm:justify-self-auto sm:px-4',
          avancados > 0 && 'border-primary text-primary',
        )}
      >
        <SlidersHorizontal />
        <span className="hidden sm:inline">Busca avancada</span>
        {avancados > 0 ? (
          <span className="rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
            {avancados}
          </span>
        ) : null}
      </Button>

      {/* `sm:contents` devolve os tres campos ao grid da tela grande; a
          unidade volta para a frente das datas, como em todo o sistema. */}
      <div className={cn(isOpen ? 'col-span-2 grid gap-3 sm:contents' : 'hidden')}>
        {units.length > 1 ? (
          <Autocomplete
            className="sm:order-first md:col-span-1"
            label="Unidade"
            placeholder="Todas"
            url="/api/v1/units"
            value={value.unitId ? [{ id: value.unitId, label: '' }] : []}
            onChange={(chosen) => set({ unitId: single(chosen) })}
          />
        ) : null}

        <Autocomplete
          className="col-span-2 md:col-span-1"
          label="Profissional"
          placeholder="Todos"
          url="/api/v1/users"
          params={{ status: 'active', perPage: '20' }}
          value={value.professionalId ? [{ id: value.professionalId, label: '' }] : []}
          onChange={(chosen) => set({ professionalId: single(chosen) })}
        />

        <Autocomplete
          className="col-span-2 md:col-span-1"
          label="Servico"
          placeholder="Todos"
          url="/api/v1/services"
          params={{ perPage: '20' }}
          value={value.serviceId ? [{ id: value.serviceId, label: '' }] : []}
          onChange={(chosen) => set({ serviceId: single(chosen) })}
        />
      </div>
    </div>
  );
}

/** Periodo padrao dos relatorios: o mes corrente. */
export function defaultFilters(): ReportFilters {
  const now = new Date();

  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
  };
}
